import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {Button, Size, Type} from '@Components/button';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import type {CheckoutStepProps, DownloadPurchaseResult} from '@Components/checkout-flow/checkout-flow.types';
import {CheckoutMode, CheckoutStep, PAYPAL_BUTTON_CONTAINER_ID} from '@Components/checkout-flow/checkout-flow.types';
import {selectBillingAddress} from '@Components/checkout-flow/checkout-flow-reducer';
import {deleteBillingAddress} from '@Components/checkout-flow/checkout-flow-thunk';
import {renderPaypalButton} from '@Components/checkout-flow/checkout-flow-paypal.library';
import {handleCheckoutSuccess, redirectTo} from '@Components/checkout-flow/checkout-flow-completion.library';
import {BillingAddressListItem} from '@Components/checkout-flow/components/billing-address-list-item/billing-address-list-item';
import {CheckoutInfoMessage} from '@Components/checkout-flow/components/checkout-info-message/checkout-info-message';
import {CheckoutInfoMessageTone} from '@Components/checkout-flow/components/checkout-info-message/checkout-info-message.types';
import {DOWNLOAD_ORDERED_URI} from '@Components/checkout-flow/components/billing-address-list-step/billing-address-list-step.types';
import styles from './billing-address-list-step.module.scss';

export function BillingAddressListStep({request, controller}: CheckoutStepProps): ReactElement | null {
  const dispatch = useAppDispatch();
  const dialogData = useAppSelector((state) => state.checkoutFlow.dialogData);
  const addresses = useAppSelector((state) => state.checkoutFlow.addresses);
  const selectedBillingAddressId = useAppSelector((state) => state.checkoutFlow.selectedBillingAddressId);
  const mode = useAppSelector((state) => state.checkoutFlow.mode);
  const [isPaypalReady, setIsPaypalReady] = useState(false);
  const [hasDeleteError, setHasDeleteError] = useState(false);
  const [deletingAddressId, setDeletingAddressId] = useState<number | null>(null);
  const selectedBillingAddressIdRef = useRef(selectedBillingAddressId);
  selectedBillingAddressIdRef.current = selectedBillingAddressId;
  const isPaypalButtonRenderedRef = useRef(false);

  useEffect(() => {
    if (dialogData === null || request.mode !== CheckoutMode.DOWNLOAD || mode === null || isPaypalButtonRenderedRef.current) {
      return;
    }
    isPaypalButtonRenderedRef.current = true;
    void renderPaypalButton({
      environment: dialogData.paypalEnvironment,
      containerSelector: `#${PAYPAL_BUTTON_CONTAINER_ID}`,
      request,
      getBillingAddressId: () => selectedBillingAddressIdRef.current,
      onPurchaseCompleted: (result: DownloadPurchaseResult): void => {
        if (request.onSuccess === undefined) {
          redirectTo(result.idOrder !== null && result.idOrder !== undefined ? `${DOWNLOAD_ORDERED_URI}/${result.idOrder}` : DOWNLOAD_ORDERED_URI);
          return;
        }
        handleCheckoutSuccess({
          request,
          effectiveMode: mode,
          result,
          handlers: {
            showPremiumConfirmation: controller.showPremiumConfirmation,
            showError: (message): void => {
              controller.fail(window.i18next.t('pmwjs_payment_failed'), message);
            },
            exit: controller.exit,
          },
        });
      },
    }).then(
      () => {
        setIsPaypalReady(true);
      },
      () => {
        controller.fail(window.i18next.t('pmwjs_something_went_wrong'), window.i18next.t('pmwjs_error_reload_page'));
      }
    );
  }, []);

  if (request.mode !== CheckoutMode.DOWNLOAD) {
    return null;
  }

  const onDeleteAddress = async (addressId: number): Promise<void> => {
    setHasDeleteError(false);
    setDeletingAddressId(addressId);
    try {
      await dispatch(deleteBillingAddress(addressId)).unwrap();
    } catch {
      setHasDeleteError(true);
    } finally {
      setDeletingAddressId(null);
    }
  };

  const getDeleteError = (): ReactElement | null => {
    if (!hasDeleteError) {
      return null;
    }
    return <CheckoutInfoMessage message={window.i18next.t('pmwjs_failed_to_delete_address_please_try_again')} tone={CheckoutInfoMessageTone.DANGER} className={styles.deleteError} />;
  };

  const getPaypalLoader = (): ReactElement | null => {
    if (isPaypalReady) {
      return null;
    }
    return <Button type={Type.PRIMARY} size={Size.MEDIUM} customClasses="_full-width" isLoading disabled />;
  };

  return (
    <div className={styles.container}>
      <ClickableDiv
        onClick={(): void => {
          controller.goToStep(CheckoutStep.BILLING_ADDRESS_FORM);
        }}
        className={styles.addAddressRow}
      >
        <Icon icon="icon-plus" size={IconSize.SIZE_ICON_24} className="content-body" ariaHidden />
        <Text val={window.i18next.t('pmwjs_add_new_address')} size={TextSize.LG} className="content-body" />
      </ClickableDiv>
      <ul className={styles.list} role="radiogroup">
        {addresses.map((address) => {
          return (
            <BillingAddressListItem
              key={address.id}
              address={address}
              isSelected={address.id === selectedBillingAddressId}
              isDeleting={address.id === deletingAddressId}
              onSelect={(): void => {
                dispatch(selectBillingAddress(address.id));
              }}
              onDelete={(): void => {
                void onDeleteAddress(address.id);
              }}
            />
          );
        })}
      </ul>
      {getDeleteError()}
      <div className={styles.ctaContainer}>
        {getPaypalLoader()}
        <div id={PAYPAL_BUTTON_CONTAINER_ID} className={`${styles.paypalButton} ${selectedBillingAddressId === null || !isPaypalReady ? styles.paypalButtonDisabled : ''}`} />
      </div>
    </div>
  );
}
